import { useEffect, useState } from "react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { api, type AppSummary, type StatusRow } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn, relativeTime } from "@/lib/utils";
import { regionLabel } from "@/lib/regions";
import { UptimeStrip } from "@/components/UptimeStrip";

function stateBadge(state: string | null | undefined, http: number | null | undefined) {
  if (state === "started" && http != null && http < 500) {
    return <Badge variant="good">healthy</Badge>;
  }
  if (state === "started" && http != null) {
    return <Badge variant="bad">http {http}</Badge>;
  }
  if (state === "started") {
    return <Badge variant="warn">no probe</Badge>;
  }
  if (!state) return <Badge variant="outline">no data</Badge>;
  return <Badge variant="warn">{state}</Badge>;
}

export function AppCard({ app }: { app: AppSummary }) {
  const [history, setHistory] = useState<StatusRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.status(app.name, 6);
        if (!cancelled) setHistory(r.rows);
      } catch {
        // ignore — partial state already shown
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [app.name]);

  const chartData = history
    .filter((r) => r.latency_ms != null)
    .map((r) => ({ ts: r.ts, latency: r.latency_ms }));
  const latencies = chartData.map((d) => d.latency as number).filter((n) => n != null);
  const median =
    latencies.length === 0
      ? null
      : latencies.slice().sort((a, b) => a - b)[Math.floor(latencies.length / 2)];

  return (
    <Card className="flex flex-col">
      <div className="flex items-start justify-between p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <a
              href={`https://${app.hostname}`}
              target="_blank"
              rel="noreferrer"
              className="font-semibold truncate hover:underline"
            >
              {app.name}
            </a>
            {app.env && (
              <Badge variant="outline" className="uppercase tracking-wide text-[10px]">
                {app.env}
              </Badge>
            )}
          </div>
          <div className="text-xs text-[var(--color-muted)] mt-1 font-mono">
            {app.hostname}
          </div>
        </div>
        {stateBadge(app.machine_state, app.http_status)}
      </div>

      <div className="px-5 pb-2 grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">region</div>
          <div className="font-mono text-[var(--color-fg)]">{regionLabel(app.region)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">state</div>
          <div className="font-mono text-[var(--color-fg)]">{app.machine_state ?? "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">latency p50</div>
          <div className="font-mono text-[var(--color-fg)]">{median != null ? `${median}ms` : "—"}</div>
        </div>
      </div>

      <div className={cn("h-16 px-2 pb-2", chartData.length === 0 && "opacity-30")}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <YAxis hide domain={["auto", "auto"]} />
            <Line
              type="monotone"
              dataKey="latency"
              stroke="var(--color-accent)"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="px-5 text-[10px] text-[var(--color-muted)]">
        last checked {relativeTime(app.checked_at)} • {chartData.length} samples / last 6h
      </div>

      <UptimeStrip app={app.name} />
    </Card>
  );
}
